import React, { useState } from 'react'
import {
 AlertCircle,
 Calendar,
 CheckCircle2,
 ChevronDown,
 ChevronUp,
 Clock,
 Heart,
 Leaf,
 Loader2,
 PinOff,
 Play,
 TrendingUp,
 Zap,
} from'lucide-react'
import { Button } from'../components/ui/Button'
import type {
  SkillCheckpointPrompt,
  SkillCheckpointRespondResult,
  SkillCheckpointResponseCode,
} from '../services/skillCheckpointService'
import type { HabitAdjustmentCheckin } from '../services/habitAdjustmentService'
import type { GoalReviewPrompt, GoalReviewRespondResult } from '../services/goalReviewService'
import { reasonSentence } from '../utils/todayOneClearThing'
import type { TimelineSourceEvent } from '../components/today/timelineModel'
import { FocusSetup, saveFocusPlan } from '../components/reminders/FocusSetup'

// ─── Types ───

export type TimeRange ='today' |'3days' |'7days'

export interface TodayItemLike {
 id: number | string
 title: string
 description?: string
 priority?:'low' |'medium' |'high' |'urgent'
 status?:'pending' |'in_progress' |'completed'
 estimated_duration?: number
 start_time?: string
 end_time?: string
 parent_goal_title?: string
 is_recurring?: boolean
 user_pinned_for_today?: boolean
}

export interface FocusTaskItem {
 task_id: string
 title: string
 description?: string
 status: string
 priority: string
 estimated_duration?: number
 is_recurring?: boolean
 is_scheduled_today?: boolean
 scheduled_time_slot?: string
 action_items_count: number
 action_items_completed: number
 parent_goal_title?: string
 progress?: number
 incremental?: boolean
 sessions_done?: number
 sessions_target?: number
 user_pinned_for_today?: boolean
 deadline_type?:'soft' |'hard'
 deadline_consequence?: string
 reasoning_tags: string[]
 /** YYYY-MM-DD when the task is due AFTER today — the when-chip shows
  *  "For <date>" so the user knows it's not for right now. */
 future_date_label?: string
}

export interface HabitOccurrenceItem {
 id: number | string
 /** The habit document id (for navigation to /habits/<id>). */
 habit_id?: number | string
 title: string
 status: string
 time_slot?: string
 duration_minutes: number
 minimum_viable_repetition?: string
 cue_or_anchor?: string
 master_id?: number | string
 date?: string
}

/**
 * One `data.scheduled_events[]` row, as the host page hands it to the page.
 *
 * It EXTENDS the execution timeline's own source type on purpose. The today
 * view stamps a read model on every block (orchestrator_service.ScheduledEvent
 * — constitution §3 / §6 Phase 2), and the host page used to rebuild each row
 * from an explicit key literal that predated those fields: the six read-model
 * keys were dropped at this seam, so the timeline resolved `commitment=null`
 * and `plan_role='none'` for every row and the whole Flexible/Confirmed
 * distinction — SC-14's first gating condition for shipping HOLD-first —
 * rendered nowhere, with tsc still exiting 0.
 *
 * Two properties of this declaration keep that from coming back:
 *   1. `extends TimelineSourceEvent` — a rename or a type change on the model
 *      side is a compile error HERE, so the two ends cannot drift apart.
 *   2. the six keys are re-declared REQUIRED (nullable, never optional) — a
 *      mapper that omits one no longer compiles. `null` is a legitimate value
 *      and renders as an honest unknown; silently *forgetting* the key is not.
 */
export interface TimelineEvent extends TimelineSourceEvent {
 id: string
 /** Canonical item behind the block — every row can open its item. */
 item_id?: string | null
 item_type?: 'task' | 'habit' | null
 title: string
 start_time?: string
 end_time?: string
 source: string
 color: string
 /** Not stamped by today-view; present for external/all-day rows if it ever is. */
 is_all_day?: boolean
 calendar_name?: string
 completed?: boolean
 duration_minutes?: number
 // ── The today-view read model (must be passed through, may be null) ──
 /** §2 dim 4: 'flexible' (HOLD) | 'confirmed'. Null = no reservation known. */
 commitment: string | null
 /** §3 provenance: how the commitment above came to be. */
 commitment_source: string | null
 /** The stored writer, verbatim — 'external' marks someone else's calendar. */
 block_source: string | null
 /** Ratified decision #6: the day-open snapshot's plane, not a local guess. */
 plan_role: string | null
 /** SC-13 provenance for the "plan adjusted" surface. */
 last_reconciled_at: string | null
 replacement_count: number | null
}

export interface HardDeadlineWarning {
 task_id: string
 title: string
 message: string
 level?:'error' |'critical' |'urgent' |'warning' |'info'
 consequence?: string
 days_remaining?: number
 days_overdue?: number
}

export interface CoachAlertLike {
 id: string
 title: string
 message: string
 severity?:'info' |'nudge' |'warning' |'critical'
 action?: { label: string; url?: string }
}

export interface BacklogSummary {
 pending: number
 in_progress: number
 skipped: number
 recurring_due_today: number
}

export interface TodayFocusRefinedProps {
 serverDateLabel: string
 /** Zone 1 — FIDELITY. What the user put on a clock themselves (the
  * reminder contracts they asked to be held to). Never capped, never
  * filtered, never deferred. Rendered ABOVE the one job. */
 commitments?: React.ReactNode
 /** Zone 4 — RESTRAINT. Everything yanamas proposes: admission proposals,
  * the prepared-day curtain, the release suggestion and offer, gap
  * questions. Optional by construction, and rendered BELOW the user's own
  * day so the coach can never stand between a person and their morning
  * (owner report 2026-08-30 — it used to sit above the hero card). */
 asks?: React.ReactNode
 // Pinned to 'today' (owner decision 2026-08-24: the dashboard is today-only;
 // the 3D/7D range selector was removed). Optional for back-compat.
 timeRange?: TimeRange
 onTimeRangeChange?: (range: TimeRange) => void
 onRefresh: () => void
 refreshing?: boolean

 progress: { percentage: number; completed: number; total: number; inProgress: number }
 dailyGoalLabel?: string
 completedTodayCount?: number
 isAboveAndBeyond?: boolean
 beyondCount?: number

 recoveryMode:'none' |'low_momentum'
 recoveryMessage?: string
 effectiveLimit: number
 dailyFocusLimit: number

 focusTasks: FocusTaskItem[]
 dailyHabits: TodayItemLike[]
 habitOccurrences: HabitOccurrenceItem[]
 inProgressBacklog: TodayItemLike[]
 timelineEvents: TimelineEvent[]
 completedItems: TodayItemLike[]
 completedYesterday: TodayItemLike[]
 hardDeadlineWarnings: HardDeadlineWarning[]
 backlogSummary: BacklogSummary

 carryoverItems: TodayItemLike[]
 carryoverTitle?: string
 carryoverBody?: string
 onReviewCarryover?: () => void

 alerts: CoachAlertLike[]
 onAlertAction?: (alertId: string, url?: string) => void
 habitAdjustmentCheckin?: HabitAdjustmentCheckin | null
 habitAdjustmentCheckins?: HabitAdjustmentCheckin[]
 habitAdjustmentRevisitDue?: any[]
 onHabitAdjustmentShown?: (checkinId: string) => void
 onHabitAdjustmentRespond?: (checkinId: string, response: 'adjust' | 'pause' | 'keep_as_is' | 'not_now') => Promise<any>
 onHabitAdjustmentDiagnose?: (checkinId: string, reasons: string[]) => Promise<any>
 onHabitAdjustmentChooseAction?: (decisionId: string, actionId: string, details?: Record<string, unknown>) => Promise<any>
 onHabitAdjustmentConfirm?: (decisionId: string) => Promise<any>
 onHabitAdjustmentPause?: (habitDefId: number, payload: { paused_until?: string; reason_codes?: string[]; source?: string }) => Promise<any>
 onHabitAdjustmentRestart?: (habitDefId: number) => Promise<any>
 onHabitAdjustmentArchive?: (habitDefId: number, payload: { source?: string; reason?: string }) => Promise<any>
 onHabitAdjustmentRefresh?: () => void

 dailyCheckInCompleted?: boolean
 moodSliderValue: number | null
 onMoodSliderChange: (val: number) => void
 onMoodSliderSubmit: () => void

 hasMotivationNudge?: boolean
 nudgeLoading?: boolean
 nudgeFetched?: boolean
 motivationMessage?: string
 motivationSuggestedAction?: string
 motivationStuckCount?: number
 onGetMotivationBoost?: () => void
 onViewMotivation?: () => void

 onOpenFocusMode?: () => void
 /** False when nothing is pending: the switch stays on the page, disabled,
  *  and says why (a control that comes and goes reads as removed — owner,
  *  2026-09-11). */
 focusModeAvailable?: boolean
 onOpenAiAssist?: () => void
 onStartTask?: (taskId: string) => void
 onCompleteTask?: (taskId: string) => void
 onOpenTask?: (taskId: string) => void
 onPinTask?: (taskId: string, pinned: boolean) => void
 onLogProgress?: (taskId: string) => void
 onCompleteHabit?: (taskId: string | number) => void
 onCompleteHabitOccurrence?: (occ: HabitOccurrenceItem) => void
 onFetchHabitDetail?: (id: string | number) => Promise<HabitDetailData | null>
 onToggleHabitActionItem?: (occurrenceId: string | number, actionItemId: number, done: boolean) => void
 onEscapeBacklog?: (taskId: string) => void
 onEscapeBlocked?: (taskId: string) => void
 onDeadlineAction?: (taskId: string) => void
 pinningTaskId?: string | null

 onNavigateToCreateGoal?: () => void
 onNavigateToCreateTask?: () => void
 onNavigateToSchedule?: () => void
 emptyStateMessage?: string
 loading?: boolean
 error?: string | null
 onRetry?: () => void

 goalReviewPrompts?: GoalReviewPrompt[]
 onGoalReviewShown?: (promptId: string) => void
 onGoalReviewRespond?: (promptId: string, code: 'mark_complete' | 'request_breakdown' | 'keep_going' | 'not_now') => Promise<GoalReviewRespondResult>
 onGoalReviewDone?: (result: GoalReviewRespondResult) => void

 skillCheckpoints?: SkillCheckpointPrompt[]
 onSkillCheckpointShown?: (promptId: string) => void
 onSkillCheckpointRespond?: (promptId: string, code: SkillCheckpointResponseCode) => Promise<SkillCheckpointRespondResult>
 onSkillCheckpointDone?: (result: SkillCheckpointRespondResult) => void
}

// ─── Helpers ───

export const priorityTone: Record<string, string> = {
 urgent:'bg-bad/10 text-bad',
 high:'bg-warn/10 text-warn',
 medium:'bg-brand-subtle text-brand',
 low:'bg-surface-alt text-secondary',
}

export const priorityLabel: Record<string, string> = {
 urgent:'Must Do', high:'Important', medium:'Normal', low:'Nice to Have',
}

const reasoningTagConfig: Record<string, { bg: string; text: string; label: string }> = {
 user_pinned: { bg:'bg-brand-subtle', text:'text-brand', label:'Pinned' },
 scheduled_today: { bg:'bg-brand-subtle', text:'text-brand', label:'Scheduled' },
 carryover_incremental: { bg:'bg-warn/10', text:'text-warn', label:'Continuing' },
 deadline_soon: { bg:'bg-bad/10', text:'text-bad', label:'Due Soon' },
 high_priority: { bg:'bg-warn/10', text:'text-warn', label:'Priority' },
 smallest_first: { bg:'bg-ok/10', text:'text-ok', label:'Quick Win' },
 in_progress: { bg:'bg-brand-subtle', text:'text-brand', label:'In Progress' },
}

// ─── Sub-components ───

/** The heading format the owner picked (2026-08-30): a plain-cased bold name,
 *  and one muted line under it saying what the section is. It reads as a
 *  sentence rather than a system label, and it is now the ONE heading shape on
 *  the page — zones, cards and the deadline strip all use it, so a heading
 *  always looks like a heading and never like a row of content. */
export function SectionHeading({ title, description, icon, count }: {
 title: string; description?: string; icon?: React.ReactNode; count?: number | string
}) {
 return (
 <div className="min-w-0 text-left">
 <div className="flex items-center gap-2 min-w-0">
 {icon && <span className="shrink-0" aria-hidden="true">{icon}</span>}
 <h2 className="text-app-body font-bold text-primary truncate">{title}</h2>
 {count !== undefined && (
 <span className="text-app-meta font-medium text-muted tabular-nums">{count}</span>
 )}
 </div>
 {description && <p className="text-app-micro text-muted mt-0.5">{description}</p>}
 </div>
 )
}

export function SectionCard({
 title, description, icon, right, children, defaultOpen = true, count, className ='',
}: {
 title: string; description?: string; icon: React.ReactNode; right?: React.ReactNode
 children: React.ReactNode
 defaultOpen?: boolean; count?: number | string; className?: string
}) {
 const [open, setOpen] = useState(defaultOpen)
 return (
 <section className={`bg-surface rounded-3xl shadow-sm border border-soft overflow-hidden ${className}`} aria-label={title}>
 <button
 type="button"
 onClick={() => setOpen(v => !v)}
 aria-expanded={open}
 aria-label={`${open ?'Collapse' :'Expand'} ${title}${count !== undefined ?` (${count})` :''}`}
 className="w-full min-h-12 px-5 py-3.5 flex items-center justify-between gap-3 text-left hover:bg-surface transition-colors"
 >
 <SectionHeading title={title} description={description} icon={icon} count={count} />
 <div className="flex items-center gap-2 shrink-0">
 {right}
 {open ? <ChevronUp className="w-4 h-4 text-muted" aria-hidden="true" /> : <ChevronDown className="w-4 h-4 text-muted" aria-hidden="true" />}
 </div>
 </button>
 {open && <div className="px-5 pb-5">{children}</div>}
 </section>
 )
}

// ProgressHeader (the deleted page's hero) went with it, 2026-08-29 — it had
// no other caller.
export function InlineMoodSlider({ value, onChange, onSubmit }: {
 value: number | null; onChange: (v: number) => void; onSubmit: () => void
}) {
 const moods = [
 { emoji:'😫', label:'Overwhelmed' },
 { emoji:'😔', label:'Struggling' },
 { emoji:'😐', label:'Okay' },
 { emoji:'😊', label:'Good' },
 { emoji:'🎉', label:'Great!' },
 ]
 const idx = value ?? 2
 return (
 <div className="bg-surface/10 backdrop-blur-sm rounded-xl px-3 py-2.5">
 <div className="flex items-center gap-3">
 <Heart className="w-4 h-4 text-brand flex-shrink-0" />
 <span className="text-xl" style={{ filter: value === null ?'grayscale(0.4)' :'none' }}>{moods[idx].emoji}</span>
 <div className="flex-1 min-w-0">
 <input type="range" min={0} max={4} step={1} value={idx}
 aria-label={`Mood: ${moods[idx].label}`}
 onChange={(e) => onChange(parseInt(e.target.value))}
 className="w-full h-1.5 rounded-full appearance-none cursor-pointer
 bg-soft
 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4
 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-surface
 [&::-webkit-slider-thumb]:shadow-md [&::-webkit-slider-thumb]:border
 [&::-webkit-slider-thumb]:border-brand-subtle [&::-webkit-slider-thumb]:transition-all
 [&::-webkit-slider-thumb]:hover:scale-110
 [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:rounded-full
 [&::-moz-range-thumb]:bg-surface [&::-moz-range-thumb]:shadow-md
 [&::-moz-range-thumb]:border [&::-moz-range-thumb]:border-brand-subtle" />
 <p className="text-app-micro text-on-brand/50 mt-0.5">{moods[idx].label}</p>
 </div>
 <button onClick={onSubmit}
 aria-label="Log mood"
 className="flex-shrink-0 min-h-touch px-4 py-2 rounded-lg bg-surface/20 hover:bg-surface/30 text-on-brand text-sm font-semibold transition-all active:scale-95">
 Log
 </button>
 </div>
 </div>
 )
}

export interface HabitActionItem {
 id: number
 title_snapshot?: string
 title?: string
 description_snapshot?: string
 status: string
 progress_percent?: number
 sort_order?: number
}

export interface HabitDetailData {
 action_items?: HabitActionItem[]
 notes?: Array<Record<string, unknown>>
 status?: string
 habit_title?: string
 feeling_before?: string | null
 feeling_after?: string | null
 duration_minutes?: number
 minimum_viable_repetition?: string
 cue_or_anchor?: string
}

export function HabitChip({ title, done, onComplete, onOpen, icon, habitId, onFetchDetail, onToggleActionItem, duration, cue, mvr }: {
 title: string; done: boolean; onComplete?: () => void; onOpen?: () => void; icon?: React.ReactNode
 habitId?: string | number
 onFetchDetail?: (id: string | number) => Promise<HabitDetailData | null>
 onToggleActionItem?: (occurrenceId: string | number, actionItemId: number, done: boolean) => void
 duration?: number; cue?: string; mvr?: string
}) {
 const [expanded, setExpanded] = useState(false)
 const [loading, setLoading] = useState(false)
 const [detail, setDetail] = useState<HabitDetailData | null>(null)

 const [fetchError, setFetchError] = useState(false)
 const handleExpand = async () => {
 if (done) { onOpen?.(); return }
 if (expanded) { setExpanded(false); return }
 setExpanded(true)
 if (!detail && onFetchDetail && habitId) {
 setLoading(true)
 setFetchError(false)
 try {
 const d = await onFetchDetail(habitId)
 setDetail(d)
 } catch {
 setFetchError(true)
 }
 setLoading(false)
 }
 }

 const actionItems = detail?.action_items || []

 return (
 <div className={`rounded-xl border transition-all ${
 done
 ?'bg-ok/10 border-ok'
 : expanded
 ?'bg-surface border-brand-subtle shadow-sm'
 :'bg-surface border-soft hover:border-ok hover:shadow-sm'
 }`}>
 {/* Header row */}
 <div className="flex items-center gap-2.5 p-3">
 <button onClick={onComplete}
 aria-label={done ?`${title} completed` :`Complete ${title}`}
 className={`flex-shrink-0 w-11 h-11 rounded-full border-2 flex items-center justify-center transition-all ${
 done ?'bg-ok border-ok text-on-brand' :'border-soft hover:border-ok'
 }`}>
 {done && <CheckCircle2 className="w-5 h-5" />}
 </button>
 <button onClick={handleExpand} className="flex-1 min-w-0 text-left">
 <span className={`text-sm font-medium truncate block ${done ?'line-through text-muted' :'text-primary'}`}>
 {title}
 </span>
 </button>
 {icon && <span className="flex-shrink-0 text-muted">{icon}</span>}
 {!done && (
 <button onClick={handleExpand}
 aria-label={expanded ?'Collapse details' :'Expand details'}
 aria-expanded={expanded}
 className="flex-shrink-0 min-w-touch min-h-touch flex items-center justify-center text-muted">
 {expanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
 </button>
 )}
 </div>

 {/* Expanded detail */}
 {expanded && !done && (
 <div className="px-3 pb-3 space-y-2 border-t border-soft pt-2">
 {/* Quick info pills */}
 {(duration || cue || mvr) && (
 <div className="flex flex-wrap gap-1.5">
 {duration != null && duration > 0 && (
 <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-brand-subtle text-app-micro text-brand font-medium">
 <Clock className="w-3 h-3" /> {duration}m
 </span>
 )}
 {cue && (
 <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-warn/10 text-app-micro text-warn font-medium">
 <Zap className="w-3 h-3" /> {cue}
 </span>
 )}
 {mvr && (
 <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-ok/10 text-app-micro text-ok font-medium">
 <Leaf className="w-3 h-3" /> Min: {mvr}
 </span>
 )}
 </div>
 )}

 {/* Action items */}
 {loading && (
 <div className="flex items-center gap-2 py-2 text-xs text-muted">
 <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading steps...
 </div>
 )}
 {!loading && actionItems.length > 0 && (
 <div className="space-y-1">
 <p className="text-app-micro font-semibold text-secondary uppercase tracking-wider">Steps</p>
 {actionItems.map(ai => {
 const aiDone = ai.status ==='completed' || (ai.progress_percent != null && ai.progress_percent >= 100)
 const aiTitle = ai.title_snapshot || ai.title ||'Untitled'
 return (
 <button
 key={ai.id}
 onClick={() => onToggleActionItem?.(habitId!, ai.id, !aiDone)}
 aria-label={`${aiDone ?'Uncheck' :'Check'} ${aiTitle}`}
 className={`w-full flex items-center gap-2.5 px-2 py-2.5 min-h-touch rounded-lg text-left transition-colors ${
 aiDone ?'bg-ok/10' :'hover:bg-surface-alt'
 }`}
 >
 <span className={`flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center ${
 aiDone
 ?'bg-ok border-ok text-on-brand'
 :'border-soft'
 }`}>
 {aiDone && <CheckCircle2 className="w-3.5 h-3.5" />}
 </span>
 <span className={`text-sm ${aiDone ?'line-through text-muted' :'text-primary'}`}>
 {aiTitle}
 </span>
 </button>
 )
 })}
 </div>
 )}
 {fetchError && (
 <div className="flex items-center gap-2 py-2 text-xs text-bad">
 <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
 <span>Couldn't load details.</span>
 <button onClick={handleExpand} className="underline hover:no-underline font-medium">Retry</button>
 </div>
 )}
 {!loading && !fetchError && actionItems.length === 0 && !detail && (
 <p className="text-xs text-muted italic py-1">No steps defined for this habit</p>
 )}

 {/* Action buttons */}
 <div className="flex items-center gap-2 pt-1">
 <button
 onClick={onComplete}
 aria-label={`Complete ${title}`}
 className="flex-1 flex items-center justify-center gap-1.5 min-h-touch px-4 py-2.5 rounded-lg bg-ok hover:bg-ok text-on-brand text-sm font-semibold transition-colors"
 >
 <CheckCircle2 className="w-4 h-4" /> Complete
 </button>
 {onOpen && (
 <button
 onClick={onOpen}
 aria-label={`View full details for ${title}`}
 className="min-h-touch px-4 py-2.5 rounded-lg bg-surface-alt hover:bg-surface-alt text-secondary text-sm font-medium transition-colors"
 >
 Full Details
 </button>
 )}
 </div>
 </div>
 )}
 </div>
 )
}

export function FocusTaskCard({ task, idx, props }: {
 task: FocusTaskItem; idx: number; props: TodayFocusRefinedProps; isLowMomentum: boolean
}) {
 const isActive = task.status ==='in_progress'
 // Owner ask 2026-08-30: the check-in offer must be common to ANYTHING picked
 // for focus, not only the hero card. Same component, same defaults.
 const [setupOpen, setSetupOpen] = useState(false)
 const [saving, setSaving] = useState(false)
 return (
 <div className={`relative p-4 rounded-xl border transition-all ${
 isActive
 ?'bg-brand-subtle border-brand-subtle ring-1 ring-brand'
 :'bg-surface border-soft hover:border-brand-subtle'
 }`}>
 {/* Title row */}
 <div className="flex items-start gap-3 mb-2">
 <span className="flex-shrink-0 w-7 h-7 rounded-full bg-brand flex items-center justify-center text-on-brand font-bold text-xs">
 {idx + 1}
 </span>
 <div className="flex-1 min-w-0">
 <div className="flex items-start justify-between gap-2">
 <button onClick={() => props.onOpenTask?.(task.task_id)} className="text-left min-w-0 flex-1">
 <h3 className="text-sm font-semibold text-primary leading-snug">{task.title}</h3>
 </button>
 <span className={`flex-shrink-0 px-1.5 py-0.5 rounded-md text-app-micro font-semibold ${priorityTone[task.priority] || priorityTone.medium}`}>
 {priorityLabel[task.priority] || task.priority}
 </span>
 </div>

 {/* Tags row */}
 <div className="flex items-center gap-1 flex-wrap mt-1.5">
 {task.deadline_type ==='hard' && (
 <span className="text-app-micro px-1.5 py-0.5 rounded bg-bad/10 text-bad font-bold border border-bad/30"
 title={task.deadline_consequence ||'Hard deadline'}>Hard Deadline</span>
 )}
 {task.reasoning_tags.map(tag => {
 const c = reasoningTagConfig[tag]
 if (!c) return null
 return <span key={tag} className={`px-1.5 py-0.5 text-app-micro font-medium rounded ${c.bg} ${c.text}`}>{c.label}</span>
 })}
 {(task.reasoning_tags.includes('user_pinned') || task.user_pinned_for_today) && props.onPinTask && (
 <button onClick={() => props.onPinTask!(task.task_id, false)}
 aria-label={`Unpin ${task.title}`}
 className="flex items-center gap-1 min-h-8 px-2.5 py-1 text-xs font-medium rounded bg-brand-subtle text-brand hover:bg-brand-subtle transition-colors">
 <PinOff className="w-3.5 h-3.5" /> Unpin
 </button>
 )}
 </div>

 {/* Meta row */}
 <div className="flex items-center gap-3 text-xs text-secondary mt-2 flex-wrap">
 {task.estimated_duration && <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {task.estimated_duration}m</span>}
 {task.is_scheduled_today && <span className="flex items-center gap-1 text-brand"><Calendar className="w-3.5 h-3.5" /> {task.scheduled_time_slot ||'Scheduled'}</span>}
 {task.incremental && <span className="flex items-center gap-1 text-warn"><TrendingUp className="w-3.5 h-3.5" /> Incremental</span>}
 {task.action_items_count > 0 && (
 <span className="flex items-center gap-1"><Zap className="w-3.5 h-3.5" /> {task.action_items_completed}/{task.action_items_count}</span>
 )}
 {task.sessions_done !== undefined && task.sessions_done > 0 && (
 <span className="flex items-center gap-1 text-ok">{task.sessions_done}/{task.sessions_target ||'∞'} sessions</span>
 )}
 </div>

 {/* Why this is on your day, in a sentence. Owner ask 2026-08-30: the
 reasoning was always sent (orchestrator focus_reasoning) and the page
 rendered it as a two-word badge, which names the rule without saying
 what it did. */}
 {reasonSentence(task.reasoning_tags) && (
 <p className="mt-2 text-app-micro text-secondary leading-relaxed">
 <span className="font-medium text-muted">Why today: </span>
 {reasonSentence(task.reasoning_tags)}
 </p>
 )}
 </div>
 </div>

 {/* Stuck escape hatch */}
 {isActive && (task.progress || 0) < 30 && (props.onEscapeBacklog || props.onEscapeBlocked) && (
 <div className="mt-2 ml-10 p-2.5 bg-warn/10 rounded-lg border border-warn">
 <p className="text-app-micro text-warn mb-1.5 font-medium">Feeling stuck?</p>
 <div className="flex gap-2">
 {props.onEscapeBacklog && (
 <button onClick={() => props.onEscapeBacklog!(task.task_id)}
 aria-label="Move to backlog"
 className="text-xs min-h-touch px-4 py-2 bg-surface hover:bg-surface-alt rounded-lg border border-soft transition-colors font-medium">
 Backlog
 </button>
 )}
 {props.onEscapeBlocked && (
 <button onClick={() => props.onEscapeBlocked!(task.task_id)}
 aria-label="Mark as blocked"
 className="text-xs min-h-touch px-4 py-2 bg-surface hover:bg-surface-alt rounded-lg border border-soft transition-colors font-medium">
 Blocked
 </button>
 )}
 </div>
 </div>
 )}

 {setupOpen && (
 <div className="mt-3 ml-10">
 <FocusSetup
 target={{
 id: task.task_id,
 title: task.title,
 estimatedDuration: task.estimated_duration ?? null,
 scheduledTimeSlot: task.scheduled_time_slot ?? null,
 }}
 busy={saving}
 onCancel={() => setSetupOpen(false)}
 onStart={async (plan) => {
 setSaving(true)
 try {
 if (plan) await saveFocusPlan(task.task_id, plan)
 } finally {
 setSaving(false)
 setSetupOpen(false)
 props.onStartTask?.(task.task_id)
 }
 }}
 />
 </div>
 )}

 {/* Actions */}
 <div className="flex gap-2 mt-3 ml-10">
 {props.onStartTask && !setupOpen && (
 <Button onClick={() => setSetupOpen(true)}
 aria-label={`Focus on ${task.title}`}
 className="flex-1 min-h-touch text-sm bg-brand">
 <Play className="w-4 h-4 mr-1.5 fill-current" /> Focus on This
 </Button>
 )}
 {props.onCompleteTask && (
 <Button onClick={() => props.onCompleteTask!(task.task_id)} variant="outline"
 aria-label={`Complete ${task.title}`}
 className="min-h-touch min-w-touch px-3 text-sm border-ok text-ok hover:bg-ok/10">
 <CheckCircle2 className="w-4 h-4" />
 </Button>
 )}
 </div>
 </div>
 )
}

// The default-export TodayFocusRefined page (~693 lines) was deleted 2026-08-29.
// It was never mounted — App.tsx routes Today to TodayFocus, which renders
// TodayOneClearThing — and it carried a duplicate Motivation Boost card whose
// "You're on track! / No stuck items detected." branch asserted a check that
// never ran (the endpoint behind it returns 503 in production). This module is
// now the shared TYPE + PRESENTATION library for TodayOneClearThing: the named
// exports above (SectionCard, HabitChip, FocusTaskCard, InlineMoodSlider, the
// priority maps and the prop/item types) are the whole public surface.
